import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/format";
import { OPERATION_TYPES, ENERGY_CLASSES } from "@/lib/constants";
import { PageHeader, EmptyState, OperationStatusBadge, Field } from "@/components/shared";
import PropertyForm, { EMPTY, buildPayload, propertyFromRecord, sheetCompletion } from "@/components/PropertyForm";
import AuthImage from "@/components/AuthImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Eye, Trash2, Workflow, CircleDot, Image as ImageIcon, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

function SortHead({ label, k, sort, onSort, align = "left" }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button type="button" onClick={() => onSort(k)} className={`inline-flex items-center gap-1 hover:text-primary transition-colors ${active ? "text-primary font-semibold" : ""} ${align === "right" ? "flex-row-reverse" : ""}`} data-testid={`sort-${k}`}>
        {label}<Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

export default function Properties() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [ops, setOps] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const EMPTY_FILTERS = { sqmMin: "", sqmMax: "", roomsMin: "", energy: "all", priceMin: "", priceMax: "" };
  const [flt, setFlt] = useState(EMPTY_FILTERS);
  const setF = (k, v) => setFlt((s) => ({ ...s, [k]: v }));
  const filtersActive = flt.sqmMin || flt.sqmMax || flt.roomsMin || flt.energy !== "all" || flt.priceMin || flt.priceMax;
  const [expanded, setExpanded] = useState(["censimento", "attivata"]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activateProp, setActivateProp] = useState(null);
  const [activating, setActivating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/properties"), api.get("/operations")])
      .then(([p, o]) => { setRows(p.data); setOps(o.data); })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Errore di caricamento"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); api.get("/contacts").then((r) => setContacts(r.data)); if (isAdmin) api.get("/auth/users").then((r) => setUsers(r.data)); }, [isAdmin, load]);

  const opByProp = useMemo(() => {
    const m = {};
    ops.forEach((o) => { if (o.property_id && !m[o.property_id]) m[o.property_id] = o; });
    return m;
  }, [ops]);

  const filtered = useMemo(() => {
    const num = (v) => (v === "" ? null : Number(v));
    const sqmMin = num(flt.sqmMin), sqmMax = num(flt.sqmMax);
    const roomsMin = num(flt.roomsMin), priceMin = num(flt.priceMin), priceMax = num(flt.priceMax);
    return rows.filter((r) => {
      if (search && !`${r.address} ${r.city} ${r.owner}`.toLowerCase().includes(search.toLowerCase())) return false;
      const sqm = Number(r.sqm_commercial) || 0;
      if (sqmMin != null && sqm < sqmMin) return false;
      if (sqmMax != null && sqm > sqmMax) return false;
      if (roomsMin != null && (Number(r.rooms) || 0) < roomsMin) return false;
      if (flt.energy !== "all" && (r.energy_class || "") !== flt.energy) return false;
      const price = Number(r.asking_price) || 0;
      if (priceMin != null && price < priceMin) return false;
      if (priceMax != null && price > priceMax) return false;
      return true;
    });
  }, [rows, search, flt]);

  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const val = (p) => sort.key === "completion" ? sheetCompletion(p)
      : sort.key === "asking_price" ? (Number(p.asking_price) || 0)
      : sort.key === "proposed_price" ? (Number(p.proposed_price) || 0)
      : sort.key === "sqm" ? (Number(p.sqm_commercial) || 0) : 0;
    const arr = [...filtered].sort((a, b) => val(a) - val(b));
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [filtered, sort]);

  const activatedList = useMemo(() => filtered.filter((p) => opByProp[p.id]), [filtered, opByProp]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(propertyFromRecord(p)); setOpen(true); };

  const save = async () => {
    if (!form.address.trim()) { toast.error("L'indirizzo è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editing) await api.put(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing ? "Scheda tecnica aggiornata" : "Immobile creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doActivate = async (type) => {
    if (!activateProp) return;
    setActivating(true);
    try {
      await api.post(`/properties/${activateProp.id}/activate-operation`, { type });
      toast.success(`Operazione attivata: ${type}`);
      const pid = activateProp.id;
      setActivateProp(null);
      navigate(`/immobili/${pid}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setActivating(false); }
  };

  const doDelete = async () => {
    if (!delItem) return;
    setDeleting(true);
    try { await api.delete(`/properties/${delItem.id}`); toast.success("Immobile eliminato definitivamente"); setDelItem(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setDeleting(false); }
  };

  const StatusPill = ({ p }) => opByProp[p.id]
    ? <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold" data-testid={`badge-attivata-${p.id}`}><CircleDot className="h-3 w-3" />Operazione attivata</span>
    : <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold" data-testid={`badge-libero-${p.id}`}><CircleDot className="h-3 w-3" />Libero da operazioni</span>;

  return (
    <div data-testid="properties-page">
      <PageHeader title="Immobili" subtitle={`${filtered.length} immobili nel censimento · ${activatedList.length} con operazione attiva`}>
        <Button onClick={openNew} data-testid="new-property-btn"><Plus className="h-4 w-4 mr-2" />Nuovo Immobile</Button>
      </PageHeader>

      <Card className="p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca per indirizzo, città, proprietario..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="properties-search" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 mt-3" data-testid="properties-filters">
          <Field label="Mq min"><Input type="number" min="0" placeholder="es. 60" value={flt.sqmMin} onChange={(e) => setF("sqmMin", e.target.value)} data-testid="filter-sqm-min" /></Field>
          <Field label="Mq max"><Input type="number" min="0" placeholder="es. 120" value={flt.sqmMax} onChange={(e) => setF("sqmMax", e.target.value)} data-testid="filter-sqm-max" /></Field>
          <Field label="Locali min"><Input type="number" min="0" placeholder="es. 3" value={flt.roomsMin} onChange={(e) => setF("roomsMin", e.target.value)} data-testid="filter-rooms-min" /></Field>
          <Field label="Classe energetica">
            <Select value={flt.energy} onValueChange={(v) => setF("energy", v)}>
              <SelectTrigger data-testid="filter-energy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {ENERGY_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Prezzo min (€)"><Input type="number" min="0" placeholder="es. 50000" value={flt.priceMin} onChange={(e) => setF("priceMin", e.target.value)} data-testid="filter-price-min" /></Field>
          <Field label="Prezzo max (€)"><Input type="number" min="0" placeholder="es. 200000" value={flt.priceMax} onChange={(e) => setF("priceMax", e.target.value)} data-testid="filter-price-max" /></Field>
        </div>
        {filtersActive ? (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{filtered.length} risultato/i con i filtri attivi</span>
            <Button variant="ghost" size="sm" onClick={() => setFlt(EMPTY_FILTERS)} data-testid="filter-reset">Azzera filtri</Button>
          </div>
        ) : null}
      </Card>

      <Accordion type="multiple" value={expanded} onValueChange={setExpanded} className="space-y-3" data-testid="properties-sections">
        {/* CENSIMENTO IMMOBILI */}
        <Card className="overflow-hidden">
          <AccordionItem value="censimento" className="border-b-0">
            <AccordionTrigger className="px-4 hover:no-underline" data-testid="section-censimento">
              <div className="flex items-center gap-3"><span className="font-semibold text-slate-800">Censimento immobili</span><span className="text-sm text-muted-foreground">{filtered.length}</span></div>
            </AccordionTrigger>
            <AccordionContent className="px-0">
              {filtered.length === 0 ? <div className="px-4 py-6 text-sm text-muted-foreground border-t border-border">Nessun immobile.</div> : (
                <div className="overflow-x-auto border-t border-border">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Indirizzo</TableHead><TableHead>Tipo</TableHead><TableHead>Stato operazione</TableHead>
                      <SortHead label="Scheda" k="completion" sort={sort} onSort={toggleSort} />
                      <SortHead label="Richiesto" k="asking_price" sort={sort} onSort={toggleSort} align="right" />
                      <SortHead label="Proposto" k="proposed_price" sort={sort} onSort={toggleSort} align="right" />
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sorted.map((p) => (
                        <TableRow key={p.id} data-testid={`censimento-row-${p.id}`}>
                          <TableCell className="cursor-pointer" onClick={() => navigate(`/immobili/${p.id}`)}>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-14 shrink-0 rounded-md overflow-hidden border border-border bg-muted grid place-items-center" data-testid={`cover-${p.id}`}>
                                {(p.photos || []).filter(Boolean)[0]
                                  ? <AuthImage src={(p.photos || []).filter(Boolean)[0]} className="h-full w-full object-cover" />
                                  : <ImageIcon className="h-4 w-4 text-slate-300" />}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-slate-800 truncate">{p.address}</div>
                                <div className="text-xs text-muted-foreground">{p.city}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{p.property_type}</TableCell>
                          <TableCell><StatusPill p={p} /></TableCell>
                          <TableCell>
                            {(() => { const c = sheetCompletion(p); return (
                              <div className="flex items-center gap-2 min-w-[92px]" data-testid={`completion-${p.id}`}>
                                <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full rounded-full ${c === 100 ? "bg-emerald-500" : c >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${c}%` }} />
                                </div>
                                <span className="text-xs font-semibold text-slate-500 tabular-nums">{c}%</span>
                              </div>
                            ); })()}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{eur(p.asking_price)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{eur(p.proposed_price)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!opByProp[p.id] && <Button size="sm" onClick={() => setActivateProp(p)} data-testid={`activate-op-${p.id}`}><Workflow className="h-3.5 w-3.5 mr-1" />Attiva operazione</Button>}
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/immobili/${p.id}`)} data-testid={`view-${p.id}`}><Eye className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500 hover:bg-rose-50" onClick={() => setDelItem(p)} data-testid={`delete-property-${p.id}`} title="Elimina definitivamente"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Card>

        {/* OPERAZIONE ATTIVATA */}
        <Card className="overflow-hidden">
          <AccordionItem value="attivata" className="border-b-0">
            <AccordionTrigger className="px-4 hover:no-underline" data-testid="section-attivata">
              <div className="flex items-center gap-3"><span className="font-semibold text-slate-800">Operazione attivata</span><span className="text-sm text-muted-foreground">{activatedList.length}</span></div>
            </AccordionTrigger>
            <AccordionContent className="px-0">
              {activatedList.length === 0 ? <div className="px-4 py-6 text-sm text-muted-foreground border-t border-border">Nessuna operazione attivata.</div> : (
                <div className="overflow-x-auto border-t border-border">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Indirizzo</TableHead><TableHead>Tipologia operazione</TableHead><TableHead>Stato operazione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {activatedList.map((p) => { const o = opByProp[p.id]; return (
                        <TableRow key={p.id} data-testid={`attivata-row-${p.id}`}>
                          <TableCell className="cursor-pointer" onClick={() => navigate(`/immobili/${p.id}`)}>
                            <div className="font-medium text-slate-800">{p.address}</div>
                            <div className="text-xs text-muted-foreground">{p.city}</div>
                          </TableCell>
                          <TableCell className="text-sm">{o.type || "—"}</TableCell>
                          <TableCell><OperationStatusBadge status={o.status} /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => navigate(`/operazioni/${o.id}`)} data-testid={`open-op-${p.id}`}>Apri operazione</Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => navigate(`/immobili/${p.id}`)}><Eye className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ); })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Card>
      </Accordion>
      {!loading && rows.length === 0 && <EmptyState>Nessun immobile trovato.</EmptyState>}

      {/* Activate operation type choice */}
      <Dialog open={!!activateProp} onOpenChange={(o) => !o && setActivateProp(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Attiva operazione</DialogTitle>
            <DialogDescription>Scegli la tipologia di operazione per <strong>{activateProp?.address}</strong>. La scheda tecnica deve essere compilata nei dati principali (indirizzo, comune, tipologia, proprietario/contatto e almeno un prezzo).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            {OPERATION_TYPES.map((t) => (
              <Button key={t} variant="outline" className="justify-start h-auto py-3" disabled={activating} onClick={() => doActivate(t)} data-testid={`activate-type-${t}`}>{t}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Property technical sheet (create/edit) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Scheda tecnica immobile" : "Nuovo immobile — Scheda tecnica"}</DialogTitle>
            <DialogDescription>Compila la scheda tecnica in tutte le sezioni. I campi contrassegnati con * sono obbligatori.</DialogDescription>
          </DialogHeader>
          <PropertyForm form={form} setForm={setForm} isAdmin={isAdmin} users={users} contacts={contacts} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-property-btn">{saving ? "Salvataggio..." : "Salva scheda"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina definitivamente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicura di voler eliminare definitivamente questo immobile? Tutti i dati collegati verranno rimossi e l'operazione non potrà essere recuperata.
              {delItem?.address ? <span className="block mt-2 font-medium text-slate-700">{delItem.address}</span> : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-property">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-property">{deleting ? "Eliminazione..." : "Elimina definitivamente"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
